<script lang="ts">
import vue2Dropzone from "vue2-dropzone";
import { Cropper, Preview } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import {
  CropperCoordinates,
  CropperImage,
  CropperInterface,
} from "@/types/vue-cropper";
import { defineComponent } from "vue";
import { helpers } from "@/services/helpers";
import { profileService } from "@/services/internal-api/profile.service";
import { authComputed } from "@/state/helpers";

declare interface ComponentData {
  previewWidth: number;
  showCropper: boolean;
  cropperSrc: string;
  cropperBG: string;
  uploadError: boolean;
  cropResult: {
    coordinates: CropperCoordinates | null;
    image: CropperImage | null;
  };
  dropzoneOptions: {
    maxFiles: number;
    url: string;
    maxFilesize: number;
    acceptedFiles: string;
  };
}

export default defineComponent({
  components: {
    vueDropzone: vue2Dropzone,
    Cropper,
    Preview,
  },
  props: {
    id: {
      type: String,
      default: "modal-image-upload",
    },
    copy: {
      type: String,
      default: "photo",
    },
    aspectRatio: {
      type: Number,
      default: null,
    },
    filename: {
      type: String,
      default: "",
    },
    bucketSuffix: {
      type: String,
      required: false,
    },
  },
  emits: ["imageUploaded"],
  methods: {
    closeUploader(): void {
      this.showCropper = false;
      this.$bvModal.hide(this.id);
    },
    backToUpload(): void {
      this.cropperSrc = "";
      this.showCropper = false;
    },
    sendToCropper(file: Blob): void {
      if (file.size < 10000000) {
        let reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = (): void => {
          if (reader.result) {
            this.cropperSrc = reader.result.toString();
            this.showCropper = true;
          }
        };
        reader.onerror = function (error): void {
          helpers.customSentryError("Failed to send image to cropper", "", {
            error: error,
          });
        };
      }
    },
    toggleCropperBG(): void {
      this.cropperBG = this.cropperBG === "white" ? "pp" : "white";
    },
    rotateCropper(angle: number): void {
      const cropper = this.$refs.cropper as CropperInterface;
      if (cropper) {
        cropper.rotate(angle);
      }
    },
    flipCropper(direction: string): void {
      const cropper = this.$refs.cropper as CropperInterface;
      if (cropper) {
        if (direction === "h") {
          cropper.flip(true, false);
        }

        if (direction === "v") {
          cropper.flip(false, true);
        }
      }
    },
    zoomCropper(factor: number): void {
      const cropper = this.$refs.cropper as CropperInterface;
      if (cropper) {
        cropper.zoom(factor);
      }
    },
    cropChange({
      coordinates,
      image,
    }: {
      coordinates: {
        height: number;
        width: number;
      };
      image: {
        src: string;
        width: number;
        height: number;
        transforms: {
          rotate: number;
          flip: {
            horizonal: boolean;
            vertical: boolean;
          };
        };
      };
    }): void {
      this.cropResult = { coordinates, image };
    },
    async performCrop(): Promise<void> {
      this.uploadError = false;
      const cropper = this.$refs.cropper as CropperInterface;
      if (!cropper) {
        return;
      }

      const canvas = cropper.getResult().canvas;
      if (typeof canvas === "boolean") {
        return;
      }

      const blob = await new Promise((resolve) => canvas.toBlob(resolve));

      let filename = this.getFilename;
      if (filename === "") {
        filename = Date.now().toString();
      }

      profileService
        .memberImageUpload(
          this.authUserId,
          `${this.bucketSuffix}/${filename}.png`,
          blob as Blob
        )
        .then((response) => {
          if (response.status === 200) {
            this.$emit("imageUploaded", response.data.upload);
            this.$bvModal.hide(this.id);
            this.cropperSrc = "";
            this.cropResult = { coordinates: null, image: null };
            this.showCropper = false;
          } else {
            this.uploadError = true;
          }
        })
        .catch(() => (this.uploadError = true));
    },
  },
  computed: {
    ...authComputed,
    previewAspectRatio(): number {
      if (!this.cropResult.coordinates) {
        return 1;
      }
      return (
        this.cropResult.coordinates.height / this.cropResult.coordinates.width
      );
    },
    previewHeight(): number {
      return this.previewWidth * this.previewAspectRatio;
    },
    getFilename(): string {
      if (this.filename !== "") {
        return this.filename;
      }

      return "";
    },
  },
  data(): ComponentData {
    return {
      previewWidth: 120,
      showCropper: false,
      cropperSrc: "",
      cropperBG: "white",
      uploadError: false,
      cropResult: {
        coordinates: null,
        image: null,
      },
      dropzoneOptions: {
        maxFiles: 1,
        url: "http://localhost",
        maxFilesize: 10,
        acceptedFiles: ".jpeg,.jpg,.png,.gif",
      },
    };
  },
});
</script>

<style>
.vue-advanced-cropper__foreground {
  opacity: 0;
}

.cropper {
  max-height: 50vh;
}

.cropper .vue-simple-line {
  border-color: black;
  border-style: dashed;
}

.cropper .vue-simple-handler {
  background-color: white;
  box-shadow: 0px 0px 1px 0px;
}

.vue-preview {
  margin: auto;
}

.cropper-toolbar {
  width: auto;
}

.cropper-background-toggle {
  height: 20px;
  width: 30px;
  margin: auto;
}
</style>

<template>
  <b-modal
    ref="imageUploadModal"
    centered
    hide-header
    hide-footer
    :id="id"
    size="xl"
    @hide="closeUploader"
  >
    <div class="mx-4 my-4" v-if="!showCropper">
      <div class="row">
        <div class="col-sm-6">
          <h3>Upload {{ copy }}</h3>
          <p>
            <b>Upload max. 10MB</b>
            . If you are still having issues uploading your image, please try
            reducing the dimensions of your image to less than
            <b>800 x 800px</b>
            . For help reducing the size or dimensions of your image,
            <a
              href="http://www.picresize.com/"
              target="_blank"
              title="click here"
            >
              click here
            </a>
            .
          </p>
          <b-button variant="link" @click="closeUploader()">
            <iconify-icon icon="fa6-solid:chevron-left" class="me-1" inline />
            Cancel
          </b-button>
        </div>
        <vue-dropzone
          class="col-sm-6"
          id="dropzone"
          ref="myVueDropzone"
          :use-custom-slot="true"
          :options="dropzoneOptions"
          @vdropzone-file-added="sendToCropper"
        >
          <div class="dropzone-custom-content">
            <i class="display-4 text-muted">
              <iconify-icon icon="fa6-solid:cloud" inline />
            </i>
            <h4>Drop files here or click to upload.</h4>
          </div>
        </vue-dropzone>
      </div>
    </div>
    <div class="mx-4 my-4" v-else>
      <div class="row">
        <div class="col-sm-8 pb-4 px-4">
          <h3>Crop {{ copy }}</h3>
          <cropper
            :backgroundClass="'bg-' + cropperBG"
            ref="cropper"
            class="cropper"
            :src="cropperSrc"
            :stencil-props="{
              aspectRatio: aspectRatio,
            }"
            :canvas="{
              minHeight: 0,
              minWidth: 0,
              maxHeight: 1080,
              maxWidth: 1080,
            }"
            @change="cropChange"
            :debounce="false"
            imageRestriction="none"
          />
          <div class="row justify-content-center">
            <b-button-group class="mt-2 cropper-toolbar">
              <b-button variant="light" @click="toggleCropperBG()">
                <div
                  class="border border-dark cropper-background-toggle"
                  :class="'bg-' + cropperBG"
                ></div>
              </b-button>
              <b-button variant="light" @click="rotateCropper(-90)">
                <iconify-icon icon="fa6-solid:arrow-rotate-left" inline />
              </b-button>
              <b-button variant="light" @click="rotateCropper(90)">
                <iconify-icon icon="fa6-solid:arrow-rotate-right" inline />
              </b-button>
              <b-button variant="light" @click="flipCropper('h')">
                <iconify-icon icon="fa6-solid:arrows-left-right" inline />
              </b-button>
              <b-button variant="light" @click="flipCropper('v')">
                <iconify-icon icon="fa6-solid:arrows-up-down" inline />
              </b-button>
              <b-button variant="light" @click="zoomCropper(0.8)">
                <iconify-icon icon="fa6-solid:magnifying-glass-minus" inline />
              </b-button>
              <b-button variant="light" @click="zoomCropper(1.2)">
                <iconify-icon icon="fa6-solid:magnifying-glass-plus" inline />
              </b-button>
            </b-button-group>
          </div>
        </div>
        <div class="col-sm-4 text-center">
          <h5 class="pb-2">Live Preview</h5>
          <preview
            :backgroundClass="'bg-' + cropperBG"
            :width="previewWidth"
            :height="previewHeight"
            :image="cropResult.image"
            :coordinates="cropResult.coordinates"
            class="border border-dark"
          />
        </div>
      </div>
      <div class="row">
        <b-alert
          :show="uploadError"
          variant="danger"
          dismissible
          @dismissed="uploadError = false"
        >
          <h5>Image Upload Error</h5>
          <p class="mb-0">
            There was an unexpected error cropping and setting your image.
            Please try again.
          </p>
        </b-alert>
        <div>
          <b-button class="my-4 me-2" variant="link" @click="backToUpload()">
            <iconify-icon icon="fa6-solid:chevron-left" class="me-1" inline />
            Back to Upload
          </b-button>
          <b-button variant="success" @click="performCrop()">
            Crop and Set {{ copy }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>
